// SPDX-License-Identifier: AGPL-3.0-or-later

import {ConfirmModal} from '@app/features/app/components/dialogs/ConfirmModal';
import {FeatureComparisonTable} from '@app/features/app/components/dialogs/components/FeatureComparisonTable';
import {PurchaseDisclaimer} from '@app/features/app/components/dialogs/components/PurchaseDisclaimer';
import {GiftInventoryBanner} from '@app/features/app/components/dialogs/components/plutonium/GiftInventoryBanner';
import {useCheckoutActions} from '@app/features/app/components/dialogs/components/plutonium/hooks/useCheckoutActions';
import {useCommunityActions} from '@app/features/app/components/dialogs/components/plutonium/hooks/useCommunityActions';
import {usePremiumData} from '@app/features/app/components/dialogs/components/plutonium/hooks/usePremiumData';
import {useSubscriptionActions} from '@app/features/app/components/dialogs/components/plutonium/hooks/useSubscriptionActions';
import {useSubscriptionStatus} from '@app/features/app/components/dialogs/components/plutonium/hooks/useSubscriptionStatus';
import {PurchaseDisabledWrapper} from '@app/features/app/components/dialogs/components/plutonium/PurchaseDisabledWrapper';
import {PurchaseHistorySection} from '@app/features/app/components/dialogs/components/plutonium/PurchaseHistorySection';
import {SelfServeRefundSection} from '@app/features/app/components/dialogs/components/plutonium/SelfServeRefundSection';
import {SubscriptionCard} from '@app/features/app/components/dialogs/components/plutonium/SubscriptionCard';
import {PREMIUM_PRODUCT_FULL_NAME, PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import GeoIP from '@app/features/app/state/GeoIP';
import {Limits} from '@app/features/app/utils/UserLimits';
import Guilds from '@app/features/guild/state/Guilds';
import {formatFileSize} from '@app/features/messaging/utils/FileUtils';
import {ComponentBus} from '@app/features/platform/utils/ComponentBus';
import * as PremiumCommands from '@app/features/premium/commands/PremiumCommands';
import {FreeProOffer} from '@app/features/premium/components/experience/FreeProOffer';
import styles from '@app/features/premium/components/experience/PremiumExperience.module.css';
import {PREMIUM_GIFT_CODE_INPUT_ID, PremiumGiftRedeem} from '@app/features/premium/components/experience/PremiumGiftRedeem';
import {PREMIUM_SECTION_IDS, PremiumSectionNav} from '@app/features/premium/components/experience/PremiumSectionNav';
import {openShopierAddTime} from '@app/features/premium/components/experience/ShopierAddTimeModal';
import {
	ShopierPurchaseSection,
	useShopierOverview,
} from '@app/features/premium/components/experience/ShopierPurchaseSection';
import PremiumState from '@app/features/premium/state/PremiumState';
import {
	BUY_GIFT_DESCRIPTOR,
	CLAIM_ACCOUNT_TO_PURCHASE_PREMIUM_DESCRIPTOR,
	FREE_VS_PREMIUM_DESCRIPTOR,
	ONE_TIME_PURCHASE_DESCRIPTOR,
	VERIFY_EMAIL_TO_PURCHASE_PREMIUM_DESCRIPTOR,
} from '@app/features/premium/utils/PremiumMessageDescriptors';
import {PREMIUM_PAGE_COPY} from '@app/features/premium/utils/PremiumPageCopy';
import {SHOPIER_COPY} from '@app/features/premium/utils/ShopierCopy';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Avatar} from '@app/features/ui/components/Avatar';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import Users from '@app/features/user/state/Users';
import * as LocaleUtils from '@app/features/user/utils/LocaleUtils';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {isNumericTierPerk, LIMIT_TIER_PERKS} from '@fluxer/constants/src/LimitTierPerks';
import {Trans, useLingui} from '@lingui/react/macro';
import {
	ArrowDownIcon,
	CheckIcon,
	CloudArrowUpIcon,
	CrownIcon,
	FrameCornersIcon,
	GiftIcon,
	HashIcon,
	IdentificationBadgeIcon,
	IdentificationCardIcon,
	LightningIcon,
	MonitorPlayIcon,
	RocketLaunchIcon,
	SealCheckIcon,
	SmileyIcon,
	SparkleIcon,
} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

type PremiumPlan = 'monthly' | 'yearly' | 'gift_1_month' | 'gift_1_year';

export type PremiumExperienceVariant = 'page' | 'embedded';

interface PremiumExperienceProps {
	variant?: PremiumExperienceVariant;
	defaultGiftMode?: boolean;
	showShopierHistory?: boolean;
}

interface PlanCardModel {
	id: PremiumPlan;
	title: string;
	price: string;
	period: string;
	badge?: string;
	popular?: boolean;
	bullets: Array<string>;
	buttonText: string;
}

const FALLBACK_UPLOAD_BYTES = 500 * 1024 * 1024;

function scrollToElement(element: HTMLElement | null) {
	element?.scrollIntoView({behavior: 'smooth', block: 'start'});
}

const PlanCard = observer(
	({
		plan,
		onSelect,
		loading,
		disabled,
		disabledTooltip,
		mostPopularLabel,
	}: {
		plan: PlanCardModel;
		onSelect: (plan: PremiumPlan) => void;
		loading: boolean;
		disabled: boolean;
		disabledTooltip: React.ReactNode;
		mostPopularLabel: string;
	}) => (
		<article
			className={clsx(styles.planCard, plan.popular && styles.planCardPopular)}
			data-flx="premium.premium-experience.plan-card"
		>
			{plan.popular && <span className={styles.planRibbon}>{mostPopularLabel}</span>}
			<header className={styles.planHeader}>
				<h3 className={styles.planTitle}>{plan.title}</h3>
				{plan.badge && <span className={styles.planBadge}>{plan.badge}</span>}
			</header>
			<div className={styles.planPrice}>
				<span className={styles.planAmount}>{plan.price}</span>
				<span className={styles.planPeriod}>{plan.period}</span>
			</div>
			<ul className={styles.planBullets}>
				{plan.bullets.map((bullet) => (
					<li key={bullet} className={styles.planBullet}>
						<CheckIcon weight="bold" className={styles.planBulletIcon} />
						<span>{bullet}</span>
					</li>
				))}
			</ul>
			<PurchaseDisabledWrapper disabled={disabled} tooltipText={disabledTooltip}>
				<button
					type="button"
					className={clsx(styles.button, plan.popular ? styles.buttonPrimary : styles.buttonOutline, styles.planButton)}
					onClick={() => onSelect(plan.id)}
					disabled={disabled || loading}
					aria-busy={loading || undefined}
					data-flx="premium.premium-experience.plan-card.select"
				>
					{plan.buttonText}
				</button>
			</PurchaseDisabledWrapper>
		</article>
	),
);

export const PremiumExperience = observer(
	({variant = 'page', defaultGiftMode = false, showShopierHistory = true}: PremiumExperienceProps) => {
		const {i18n} = useLingui();
		const currentUser = Users.currentUser;
		const premiumState = PremiumState.loadedForUserId === currentUser?.id ? PremiumState.state : null;
		const locale = LocaleUtils.getCurrentLocale();
		const [isGiftMode, setIsGiftMode] = useState(defaultGiftMode);
		const heroRef = useRef<HTMLElement | null>(null);
		const plansRef = useRef<HTMLElement | null>(null);
		const subscriptionRef = useRef<HTMLElement | null>(null);
		const compareRef = useRef<HTMLElement | null>(null);
		const countryCode = GeoIP.countryCode;
		const guilds = Guilds.getGuilds();
		const visionaryGuild = useMemo(() => guilds.find((guild) => guild.features.has(GuildFeatures.VISIONARY)), [guilds]);
		const subscriptionStatus = useSubscriptionStatus(currentUser, premiumState);
		const {
			priceIds,
			monthlyPrice,
			yearlyPrice,
			giftMonthlyPrice,
			giftYearlyPrice,
			currentSubscriptionPrice,
			currentSubscriptionPriceLabel,
			currentSubscriptionListPriceLabel,
			isCurrentSubscriptionGrandfathered,
		} = usePremiumData({premiumState});
		const {
			loadingPortal,
			loadingCancel,
			loadingReactivate,
			loadingEndGrace,
			loadingChangeBillingCycle,
			loadingCancelPendingChange,
			handleOpenCustomerPortal,
			handleCancelSubscription,
			handleEndPremiumGracePeriod,
			handleReactivateSubscription,
			handleChangeSubscriptionBillingCycle,
			handleCancelPendingSubscriptionChange,
		} = useSubscriptionActions(countryCode);
		const {loadingRejoinCommunity, handleCommunityButtonClick} = useCommunityActions(visionaryGuild);
		const {loadingCheckout, handleSelectPlan} = useCheckoutActions(
			priceIds,
			countryCode,
			subscriptionStatus.isGiftSubscription,
			MobileLayout.enabled,
		);
		const {overview: shopierOverview, refresh: refreshShopierOverview} = useShopierOverview(Boolean(currentUser?.id));
		const shopierEnabled = shopierOverview?.enabled === true;
		useEffect(() => {
			if (!currentUser?.id) return;
			void PremiumCommands.refreshPremiumState(countryCode ?? undefined);
		}, [countryCode, currentUser?.id]);
		useEffect(() => {
			if (!defaultGiftMode) return;
			const frame = requestAnimationFrame(() => scrollToElement(plansRef.current));
			return () => cancelAnimationFrame(frame);
		}, [defaultGiftMode]);

		const isClaimed = currentUser?.isClaimed() ?? false;
		const isEmailVerified = currentUser?.verified === true;
		const purchaseDisabled = !isClaimed || !isEmailVerified;
		const purchaseDisabledTooltip = !isClaimed
			? i18n._(CLAIM_ACCOUNT_TO_PURCHASE_PREMIUM_DESCRIPTOR, {premiumProductFullName: PREMIUM_PRODUCT_FULL_NAME})
			: i18n._(VERIFY_EMAIL_TO_PURCHASE_PREMIUM_DESCRIPTOR, {premiumProductFullName: PREMIUM_PRODUCT_FULL_NAME});
		const handleSelectPlanGuarded = useCallback(
			(plan: PremiumPlan) => {
				if (purchaseDisabled) return;
				handleSelectPlan(plan);
			},
			[handleSelectPlan, purchaseDisabled],
		);
		const scrollToCompare = useCallback(() => scrollToElement(compareRef.current), []);
		// The gift code box lives on this page; the settings-tab event only works inside the settings modal.
		const navigateToRedeemGift = useCallback(() => {
			const input = document.getElementById(PREMIUM_GIFT_CODE_INPUT_ID);
			if (!input) {
				ComponentBus.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'gift_inventory'});
				return;
			}
			input.scrollIntoView({behavior: 'smooth', block: 'center'});
			input.focus({preventScroll: true});
		}, []);
		const handleCancelSubscriptionConfirmed = useCallback(() => {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={<Trans>Cancel subscription?</Trans>}
						description={
							<Trans>
								You keep your perks until your next renewal date, then have a 3-day grace period to resubscribe and keep
								your subscriber history.
							</Trans>
						}
						primaryText={<Trans>Cancel subscription</Trans>}
						primaryVariant="danger"
						secondaryText={<Trans>Keep subscription</Trans>}
						onPrimary={async () => {
							await handleCancelSubscription();
						}}
						data-flx="premium.premium-experience.cancel-subscription.confirm-modal"
					/>
				)),
			);
		}, [handleCancelSubscription]);
		const handleEndPremiumGracePeriodConfirmed = useCallback(() => {
			ModalCommands.push(
				modal(() => (
					<ConfirmModal
						title={<Trans>End grace period now?</Trans>}
						description={
							<Trans>
								You lose all {PREMIUM_PRODUCT_NAME} perks immediately and your subscriber history resets. This cannot be
								undone.
							</Trans>
						}
						primaryText={<Trans>End grace period</Trans>}
						primaryVariant="danger"
						secondaryText={<Trans>Keep grace period</Trans>}
						onPrimary={async () => {
							await handleEndPremiumGracePeriod();
						}}
						data-flx="premium.premium-experience.end-grace-period.confirm-modal"
					/>
				)),
			);
		}, [handleEndPremiumGracePeriod]);

		const uploadSizeLabel = useMemo(() => {
			const perk = LIMIT_TIER_PERKS.find((candidate) => candidate.i18nKey === 'file_upload_size');
			if (!perk || !isNumericTierPerk(perk)) return formatFileSize(i18n.locale, FALLBACK_UPLOAD_BYTES);
			const value = perk.limitKey ? Limits.getStockValue(perk.limitKey, perk.stockValue) : perk.stockValue;
			return formatFileSize(i18n.locale, value);
		}, [i18n.locale]);

		const perks = useMemo(
			() => [
				{
					key: 'animated',
					tone: styles.toneSunset,
					icon: SparkleIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkAnimatedTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkAnimatedBody),
				},
				{
					key: 'upload',
					tone: styles.toneOcean,
					icon: CloudArrowUpIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkUploadTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkUploadBody, {size: uploadSizeLabel}),
				},
				{
					key: 'video',
					tone: styles.toneViolet,
					icon: MonitorPlayIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkVideoTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkVideoBody),
				},
				{
					key: 'emoji',
					tone: styles.toneGold,
					icon: SmileyIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkEmojiTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkEmojiBody),
				},
				{
					key: 'tag',
					tone: styles.toneRose,
					icon: HashIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkTagTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkTagBody),
				},
				{
					key: 'profiles',
					tone: styles.toneMint,
					icon: IdentificationCardIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkProfilesTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkProfilesBody),
				},
				{
					key: 'boost',
					tone: styles.toneViolet,
					icon: LightningIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkBoostTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkBoostBody),
				},
				{
					key: 'avatar-frame',
					tone: styles.toneRose,
					icon: FrameCornersIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkAvatarFrameTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkAvatarFrameBody),
				},
				{
					key: 'nameplate',
					tone: styles.toneOcean,
					icon: IdentificationBadgeIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkNameplateTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkNameplateBody),
				},
				{
					key: 'badge',
					tone: styles.toneGold,
					icon: SealCheckIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkBadgeTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkBadgeBody),
				},
				{
					key: 'early',
					tone: styles.toneSunset,
					icon: RocketLaunchIcon,
					title: i18n._(PREMIUM_PAGE_COPY.perkEarlyTitle),
					body: i18n._(PREMIUM_PAGE_COPY.perkEarlyBody),
				},
			],
			[i18n.locale, uploadSizeLabel],
		);

		const showSubscription = subscriptionStatus.shouldShowPremiumCard;
		const showGiftPlans = !shopierEnabled && (showSubscription || isGiftMode);
		const plans: Array<PlanCardModel> = useMemo(() => {
			if (showGiftPlans) {
				const giftBullets = [i18n._(PREMIUM_PAGE_COPY.oneTimeNoRenewal), i18n._(PREMIUM_PAGE_COPY.deliveredAsCode)];
				return [
					{
						id: 'gift_1_month',
						title: i18n._(PREMIUM_PAGE_COPY.gift1Month),
						price: giftMonthlyPrice,
						period: i18n._(ONE_TIME_PURCHASE_DESCRIPTOR),
						popular: true,
						bullets: giftBullets,
						buttonText: i18n._(BUY_GIFT_DESCRIPTOR),
					},
					{
						id: 'gift_1_year',
						title: i18n._(PREMIUM_PAGE_COPY.gift1Year),
						price: giftYearlyPrice,
						period: i18n._(ONE_TIME_PURCHASE_DESCRIPTOR),
						badge: i18n._(PREMIUM_PAGE_COPY.save17),
						bullets: giftBullets,
						buttonText: i18n._(BUY_GIFT_DESCRIPTOR),
					},
				];
			}
			return [
				{
					id: 'monthly',
					title: i18n._(PREMIUM_PAGE_COPY.monthly),
					price: monthlyPrice,
					period: i18n._(PREMIUM_PAGE_COPY.perMonth),
					bullets: [
						i18n._(PREMIUM_PAGE_COPY.allPerksIncluded),
						i18n._(PREMIUM_PAGE_COPY.billedMonthly),
						i18n._(PREMIUM_PAGE_COPY.cancelAnytime),
					],
					buttonText: i18n._(PREMIUM_PAGE_COPY.subscribe),
				},
				{
					id: 'yearly',
					title: i18n._(PREMIUM_PAGE_COPY.yearly),
					price: yearlyPrice,
					period: i18n._(PREMIUM_PAGE_COPY.perYear),
					badge: i18n._(PREMIUM_PAGE_COPY.save17),
					popular: true,
					bullets: [
						i18n._(PREMIUM_PAGE_COPY.allPerksIncluded),
						i18n._(PREMIUM_PAGE_COPY.billedYearly),
						i18n._(PREMIUM_PAGE_COPY.cancelAnytime),
					],
					buttonText: i18n._(PREMIUM_PAGE_COPY.upgradeNow),
				},
			];
		}, [i18n.locale, showGiftPlans, monthlyPrice, yearlyPrice, giftMonthlyPrice, giftYearlyPrice]);

		if (!currentUser) return null;

		const isPremium = subscriptionStatus.isPremium;
		const openPlans = (giftMode: boolean) => {
			setIsGiftMode(giftMode);
			requestAnimationFrame(() => scrollToElement(plansRef.current));
		};
		const premiumNameArgs = {premiumProductName: PREMIUM_PRODUCT_NAME};
		const bottomPlans = plans.slice().sort((a, b) => Number(Boolean(b.popular)) - Number(Boolean(a.popular)));

		return (
			<div
				className={clsx(styles.root, variant === 'page' ? styles.variantPage : styles.variantEmbedded)}
				data-flx="premium.premium-experience.root"
			>
				{variant === 'embedded' && (
					<div className={styles.topNav} data-flx="premium.premium-experience.top-nav">
						<span className={styles.topNavBrand}>
							<CrownIcon weight="fill" className={styles.topNavBrandIcon} />
							{PREMIUM_PRODUCT_NAME}
						</span>
						<PremiumSectionNav />
					</div>
				)}

				<GiftInventoryBanner currentUser={currentUser} />

				<section
					ref={heroRef}
					id={PREMIUM_SECTION_IDS.home}
					className={styles.hero}
					data-flx="premium.premium-experience.hero"
				>
					<div className={styles.heroBackdrop} aria-hidden="true" />
					<div className={styles.heroCopy}>
						<span className={styles.eyebrow}>
							<CrownIcon weight="fill" className={styles.eyebrowIcon} />
							{PREMIUM_PRODUCT_FULL_NAME}
						</span>
						<h1 className={styles.heroTitle}>
							{isPremium
								? i18n._(PREMIUM_PAGE_COPY.heroActiveTitle, premiumNameArgs)
								: i18n._(PREMIUM_PAGE_COPY.heroTitle)}
						</h1>
						<p className={styles.heroSubtitle}>
							{isPremium ? i18n._(PREMIUM_PAGE_COPY.heroActiveSubtitle) : i18n._(PREMIUM_PAGE_COPY.heroSubtitle)}
						</p>
						<div className={styles.heroActions}>
							{isPremium ? (
								<button
									type="button"
									className={clsx(styles.button, styles.buttonPrimary)}
									onClick={() =>
										shopierEnabled && shopierOverview && !subscriptionStatus.isVisionary
											? openShopierAddTime(shopierOverview.plans, 'yearly', () => void refreshShopierOverview())
											: scrollToElement(subscriptionRef.current)
									}
									data-flx="premium.premium-experience.hero.manage"
								>
									<CrownIcon weight="fill" className={styles.buttonIcon} />
									{shopierEnabled && !subscriptionStatus.isVisionary
										? i18n._(SHOPIER_COPY.addTime)
										: i18n._(PREMIUM_PAGE_COPY.managePlan)}
								</button>
							) : (
								<button
									type="button"
									className={clsx(styles.button, styles.buttonPrimary)}
									onClick={() => openPlans(false)}
									data-flx="premium.premium-experience.hero.subscribe"
								>
									<CrownIcon weight="fill" className={styles.buttonIcon} />
									{i18n._(PREMIUM_PAGE_COPY.subscribe)}
								</button>
							)}
							{!shopierEnabled && (
								<button
									type="button"
									className={clsx(styles.button, styles.buttonGlass)}
									onClick={() => openPlans(true)}
									data-flx="premium.premium-experience.hero.gift"
								>
									<GiftIcon weight="fill" className={styles.buttonIcon} />
									{i18n._(PREMIUM_PAGE_COPY.gift)}
								</button>
							)}
						</div>
						<button
							type="button"
							className={styles.heroLink}
							onClick={scrollToCompare}
							data-flx="premium.premium-experience.hero.compare"
						>
							{i18n._(PREMIUM_PAGE_COPY.comparePlans)}
							<ArrowDownIcon weight="bold" className={styles.heroLinkIcon} />
						</button>
					</div>
					<div className={styles.heroArt} aria-hidden="true">
						<span className={clsx(styles.floatChip, styles.floatChipTop)}>
							<MonitorPlayIcon weight="fill" />
							<span>4K · 60fps</span>
						</span>
						<span className={clsx(styles.floatChip, styles.floatChipBottom)}>
							<CloudArrowUpIcon weight="fill" />
							{uploadSizeLabel}
						</span>
						<div className={styles.previewCard}>
							<div className={styles.previewBanner} />
							<div className={styles.previewAvatarRing}>
								<Avatar user={currentUser} size={72} showOffline={false} />
							</div>
							<div className={styles.previewBody}>
								<div className={styles.previewNameRow}>
									<span className={styles.previewName}>{currentUser.displayName}</span>
									<span className={styles.previewBadge}>
										<CrownIcon weight="fill" />
									</span>
								</div>
								<span className={styles.previewTag}>{currentUser.tag}</span>
								<span className={styles.previewPill}>
									<SparkleIcon weight="fill" size={12} className={styles.previewPillIcon} />
									{PREMIUM_PRODUCT_NAME} {i18n._(PREMIUM_PAGE_COPY.memberPill)}
								</span>
							</div>
						</div>
					</div>
				</section>

				{!isPremium && <FreeProOffer />}

				{showSubscription && (
					<section
						ref={subscriptionRef}
						className={styles.section}
						data-flx="premium.premium-experience.subscription-section"
					>
						<header className={styles.sectionHeader}>
							<h2 className={styles.sectionTitle}>{i18n._(PREMIUM_PAGE_COPY.subscriptionTitle)}</h2>
						</header>
						<SubscriptionCard
							locale={locale}
							isVisionary={subscriptionStatus.isVisionary}
							perksDisabled={subscriptionStatus.perksDisabled}
							isGiftSubscription={subscriptionStatus.isGiftSubscription}
							premiumUntil={subscriptionStatus.actualPremiumUntil}
							billingCycle={subscriptionStatus.billingCycle}
							monthlyPrice={monthlyPrice}
							yearlyPrice={yearlyPrice}
							monthlyAmountMinor={priceIds?.monthly_amount_minor ?? null}
							yearlyAmountMinor={priceIds?.yearly_amount_minor ?? null}
							priceCurrency={priceIds?.currency ?? null}
							currentSubscriptionAmountMinor={currentSubscriptionPrice?.amount_minor ?? null}
							currentSubscriptionCurrency={currentSubscriptionPrice?.currency ?? null}
							currentSubscriptionPriceLabel={currentSubscriptionPriceLabel}
							currentSubscriptionListPriceLabel={currentSubscriptionListPriceLabel}
							isCurrentSubscriptionGrandfathered={isCurrentSubscriptionGrandfathered}
							pendingSubscriptionChange={premiumState?.billing.pending_subscription_change ?? null}
							gracePeriodInfo={subscriptionStatus.gracePeriodInfo}
							premiumWillCancel={subscriptionStatus.premiumWillCancel}
							hasEverPurchased={subscriptionStatus.hasEverPurchased}
							shouldUseCancelQuickAction={subscriptionStatus.shouldUseCancelQuickAction}
							shouldUseReactivateQuickAction={subscriptionStatus.shouldUseReactivateQuickAction}
							shouldUseChangePlanQuickAction={subscriptionStatus.shouldUseChangePlanQuickAction}
							loadingPortal={loadingPortal}
							loadingCancel={loadingCancel}
							loadingReactivate={loadingReactivate}
							loadingEndGrace={loadingEndGrace}
							loadingChangeBillingCycle={loadingChangeBillingCycle}
							loadingCancelPendingChange={loadingCancelPendingChange}
							loadingRejoinCommunity={loadingRejoinCommunity}
							scrollToPerks={scrollToCompare}
							navigateToRedeemGift={navigateToRedeemGift}
							handleOpenCustomerPortal={handleOpenCustomerPortal}
							handleReactivateSubscription={handleReactivateSubscription}
							handleCancelSubscription={handleCancelSubscriptionConfirmed}
							handleEndPremiumGracePeriod={handleEndPremiumGracePeriodConfirmed}
							handleChangeSubscriptionBillingCycle={handleChangeSubscriptionBillingCycle}
							handleCancelPendingSubscriptionChange={handleCancelPendingSubscriptionChange}
							handleCommunityButtonClick={handleCommunityButtonClick}
							purchaseDisabled={purchaseDisabled}
							purchaseDisabledTooltip={purchaseDisabledTooltip}
						/>
						<PurchaseDisclaimer align="center" isPremium />
					</section>
				)}

				<section className={styles.section} data-flx="premium.premium-experience.perks-section">
					<header className={styles.sectionHeader}>
						<h2 className={styles.sectionTitle}>{i18n._(PREMIUM_PAGE_COPY.perksTitle)}</h2>
						<p className={styles.sectionSubtitle}>{i18n._(PREMIUM_PAGE_COPY.perksSubtitle)}</p>
					</header>
					<div className={styles.perkGrid}>
						{perks.map(({key, tone, icon: Icon, title, body}) => (
							<article key={key} className={clsx(styles.perkCard, tone)}>
								<span className={styles.perkIcon}>
									<Icon weight="fill" />
								</span>
								<h3 className={styles.perkTitle}>{title}</h3>
								<p className={styles.perkBody}>{body}</p>
							</article>
						))}
					</div>
				</section>

				<section
					ref={plansRef}
					id={PREMIUM_SECTION_IDS.plans}
					className={styles.section}
					data-flx="premium.premium-experience.plans-section"
				>
					<header className={styles.sectionHeader} hidden={shopierEnabled}>
						<h2 className={styles.sectionTitle}>
							{showGiftPlans
								? i18n._(PREMIUM_PAGE_COPY.giftPlansTitle, premiumNameArgs)
								: i18n._(PREMIUM_PAGE_COPY.plansTitle)}
						</h2>
						<p className={styles.sectionSubtitle}>
							{showGiftPlans ? i18n._(PREMIUM_PAGE_COPY.giftPlansSubtitle) : i18n._(PREMIUM_PAGE_COPY.plansSubtitle)}
						</p>
					</header>
					{!showSubscription && !shopierEnabled && (
						<div className={styles.segmented} role="group" aria-label={i18n._(PREMIUM_PAGE_COPY.purchaseMode)}>
							<button
								type="button"
								className={clsx(styles.segment, !isGiftMode && styles.segmentActive)}
								aria-pressed={!isGiftMode}
								onClick={() => setIsGiftMode(false)}
								data-flx="premium.premium-experience.mode.self"
							>
								<CrownIcon weight="fill" />
								{i18n._(PREMIUM_PAGE_COPY.forMe)}
							</button>
							<button
								type="button"
								className={clsx(styles.segment, isGiftMode && styles.segmentActive)}
								aria-pressed={isGiftMode}
								onClick={() => setIsGiftMode(true)}
								data-flx="premium.premium-experience.mode.gift"
							>
								<GiftIcon weight="fill" />
								{i18n._(PREMIUM_PAGE_COPY.asAGift)}
							</button>
						</div>
					)}
					{shopierEnabled && shopierOverview ? (
						<ShopierPurchaseSection
							overview={shopierOverview}
							disabled={purchaseDisabled}
							disabledTooltip={purchaseDisabledTooltip}
							onRefresh={refreshShopierOverview}
							showHistory={showShopierHistory}
							isPremium={subscriptionStatus.isPremium}
							isLifetime={subscriptionStatus.isVisionary}
						/>
					) : (
						<div className={styles.planGrid}>
							{plans.map((plan) => (
								<PlanCard
									key={plan.id}
									plan={plan}
									onSelect={handleSelectPlanGuarded}
									loading={loadingCheckout}
									disabled={purchaseDisabled}
									disabledTooltip={purchaseDisabledTooltip}
									mostPopularLabel={i18n._(PREMIUM_PAGE_COPY.mostPopular)}
								/>
							))}
						</div>
					)}
					<PremiumGiftRedeem />
					{!showSubscription && <PurchaseDisclaimer />}
				</section>

				{subscriptionStatus.hasEverPurchased && (
					<section className={styles.section} data-flx="premium.premium-experience.billing-section">
						<PurchaseHistorySection
							premiumState={premiumState}
							loadingPortal={loadingPortal}
							handleOpenCustomerPortal={handleOpenCustomerPortal}
						/>
						<SelfServeRefundSection
							eligibility={premiumState?.billing.refund_eligibility ?? null}
							refreshPremiumState={() => PremiumCommands.refreshPremiumState(countryCode ?? undefined)}
						/>
					</section>
				)}

				<section
					ref={compareRef}
					id={PREMIUM_SECTION_IDS.compare}
					className={styles.section}
					data-flx="premium.premium-experience.compare-section"
				>
					<header className={styles.sectionHeader}>
						<h2 className={styles.sectionTitle}>{i18n._(FREE_VS_PREMIUM_DESCRIPTOR, premiumNameArgs)}</h2>
						<p className={styles.sectionSubtitle}>{i18n._(PREMIUM_PAGE_COPY.compareSubtitle)}</p>
					</header>
					<FeatureComparisonTable />
				</section>

				{!isPremium && !shopierEnabled && (
					<section className={styles.finale} data-flx="premium.premium-experience.finale">
						<div className={styles.heroBackdrop} aria-hidden="true" />
						<div className={styles.finaleCopy}>
							<h2 className={styles.finaleTitle}>
								{isGiftMode ? i18n._(PREMIUM_PAGE_COPY.bottomGiftTitle) : i18n._(PREMIUM_PAGE_COPY.bottomTitle)}
							</h2>
							<p className={styles.finaleSubtitle}>
								{isGiftMode
									? i18n._(PREMIUM_PAGE_COPY.bottomGiftSubtitle, premiumNameArgs)
									: i18n._(PREMIUM_PAGE_COPY.bottomSubtitle, premiumNameArgs)}
							</p>
						</div>
						<div className={styles.finaleActions}>
							{bottomPlans.map((plan, index) => (
								<PurchaseDisabledWrapper
									key={plan.id}
									disabled={purchaseDisabled}
									tooltipText={purchaseDisabledTooltip}
								>
									<button
										type="button"
										className={clsx(styles.button, index === 0 ? styles.buttonPrimary : styles.buttonGlass)}
										onClick={() => handleSelectPlanGuarded(plan.id)}
										disabled={purchaseDisabled || loadingCheckout}
										aria-busy={loadingCheckout || undefined}
										data-flx="premium.premium-experience.finale.select"
									>
										{plan.title} · {plan.price}
									</button>
								</PurchaseDisabledWrapper>
							))}
						</div>
					</section>
				)}
			</div>
		);
	},
);
